import { getLocalStorage } from "@elit-hair/elit-tools";

import { CALL_STATUS } from "@/constants/call";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { ProviderConfig, TelephonyProvider } from "@/features/telephony/interfaces/telephony-provider.interface";
import { TelephonyEventBus } from "@/features/telephony/services/telephony-event-bus";
import { TelephonyStateStore } from "@/features/telephony/services/telephony-state.store";
import { TelephonyEventType } from "@/features/telephony/types/telephony-events.types";
import { audioProcessingService } from "@/services/audio-processing-service";
import { CallFailureType, CallPayload, CallStatus } from "@/types/call.types";
import { getVoiceRegistrationToken } from "@/utils/voice-registration.utils";

type FailureHandler = (failureType: CallFailureType, sessionId: string) => void;

export class CallManager {
    private failureHandler: FailureHandler | null = null;
    private statusHandler: ((status: CallStatus) => void) | null = null;
    private audioLevelCleanup: (() => void) | null = null;
    private unsubscribeProvider: (() => void) | null = null;

    constructor(
        private readonly provider: TelephonyProvider,
        private readonly eventBus: TelephonyEventBus,
        private readonly stateStore: TelephonyStateStore
    ) {
        this.unsubscribeProvider = this.provider.subscribe((event) => {
            this.eventBus.emit(event);

            switch (event.type) {
                case TelephonyEventType.PROVIDER_INITIALIZED:
                    this.stateStore.patch({ providerInitialized: true, providerId: event.providerId });
                    break;
                case TelephonyEventType.PROVIDER_DESTROYED:
                    this.stateStore.patch({ providerInitialized: false });
                    break;
                case TelephonyEventType.CALL_CONNECTING:
                case TelephonyEventType.CALL_RINGING:
                case TelephonyEventType.CALL_CONNECTED:
                case TelephonyEventType.CALL_DISCONNECTED:
                    this.stateStore.patch({
                        callStatus: event.state,
                        activeSessionId: event.sessionId,
                    });
                    this.statusHandler?.(event.state);
                    break;
                case TelephonyEventType.CALL_FAILED:
                    this.stateStore.patch({
                        callStatus: event.state,
                        activeSessionId: event.sessionId,
                    });
                    this.statusHandler?.(event.state);
                    this.failureHandler?.(event.failureType, event.sessionId);
                    break;
                case TelephonyEventType.CALL_MUTE_CHANGED:
                    this.stateStore.patch({ isMuted: event.muted });
                    break;
                case TelephonyEventType.AUDIO_LEVEL_UPDATED:
                    this.stateStore.patch({ audioLevels: event.levels });
                    break;
                default:
                    break;
            }
        });
    }

    setStatusHandler(handler: (status: CallStatus) => void): void {
        this.statusHandler = handler;
    }

    setFailureHandler(handler: FailureHandler): void {
        this.failureHandler = handler;
    }

    get initialized(): boolean {
        return this.provider.initialized;
    }

    async initialize(config: ProviderConfig): Promise<void> {
        await this.provider.initialize(config);
    }

    async startCall(params: CallPayload): Promise<void> {
        if (!this.provider.initialized) {
            const registrationToken = getVoiceRegistrationToken(
                getLocalStorage(LOCAL_STORAGE_KEYS.FCM_TOKEN.key, LOCAL_STORAGE_KEYS.FCM_TOKEN.default)
            );
            if (!registrationToken) {
                throw new Error("Telephony provider not initialized and voice registration token not found");
            }
            await this.initialize({ registrationToken });
        }

        await this.provider.connect(params);
    }

    async endCall(): Promise<void> {
        this.stopAudioLevelMonitoring();
        await this.provider.disconnect();
        audioProcessingService.releaseCallStreams();
        this.stateStore.patch({
            callStatus: CALL_STATUS.DISCONNECTED,
            activeSessionId: null,
            isMuted: false,
            audioLevels: [],
        });
        this.statusHandler?.(CALL_STATUS.DISCONNECTED);
    }

    sendDigits(digits: string): void {
        this.provider.sendDigits(digits);
    }

    async toggleMute(muted?: boolean): Promise<boolean> {
        const nextMuted = muted ?? !this.provider.isMuted();
        const success = await this.provider.mute(nextMuted);
        if (success) {
            this.stateStore.patch({ isMuted: nextMuted });
        }
        return success;
    }

    isMuted(): boolean {
        return this.provider.isMuted();
    }

    async setInputDevice(deviceId: string): Promise<void> {
        await this.provider.setInputDevice(deviceId);
    }

    async setOutputDevice(deviceId: string): Promise<void> {
        await this.provider.setOutputDevice(deviceId);
    }

    async releaseMicrophoneAccess(): Promise<void> {
        await this.provider.releaseMicrophoneAccess();
        audioProcessingService.releaseCallStreams();
    }

    setOutgoingCallMelodyMuted(muted: boolean): void {
        this.provider.setOutgoingCallMelodyMuted(muted);
    }

    startAudioLevelMonitoring(onLevels?: (levels: number[]) => void): void {
        this.stopAudioLevelMonitoring();
        this.audioLevelCleanup =
            this.provider.startAudioLevelMonitoring((levels) => {
                this.stateStore.patch({ audioLevels: levels });
                onLevels?.(levels);
                this.eventBus.emit({
                    type: TelephonyEventType.AUDIO_LEVEL_UPDATED,
                    levels,
                });
            }) ?? null;
    }

    stopAudioLevelMonitoring(): void {
        this.audioLevelCleanup?.();
        this.audioLevelCleanup = null;
    }

    destroy(): void {
        this.stopAudioLevelMonitoring();
        this.unsubscribeProvider?.();
        void this.provider.destroy();
    }
}
